import React from 'react';
import { Button } from '@oysterjs/ui/button';
import { Slideout } from '@oysterjs/ui/slideout';
import { Spinner } from '@oysterjs/ui/spinner';

import { useCaraWorkspaceSubscription } from '~/api/data/workspaces/use-cara-workspace-subscription';
import { type CaraWorkspaceFieldsFragment, useGetCaraWorkspaceQuery } from '~/api/graphql';
import { InlineNotice } from '~/components/inline-notice';
import { ArtifactFocusDialog } from '~/components/workspaces/artifact-focus';
import { useArtifactFocus } from '~/components/workspaces/hooks/use-artifact-focus';
import { useMailboxComposerEnabled } from '~/components/workspaces/hooks/use-mailbox-composer-enabled';
import { newestArtifactVersion } from '~/components/workspaces/lib/artifact';
import { MAILBOX_SETTINGS_PATH } from '~/components/mailbox/bulk-send-paths';

const LoadingEmail = () => (
  <span
    role="status"
    className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400"
  >
    <Spinner size="size-4" color="neutral" />
    Loading email…
  </span>
);

/**
 * One email of a bulk draft in the composer. It reads the workspace the email lives in and
 * follows it live, as the workspace page does, so an edit saves and a Cara redraft shows.
 */
export const BulkDraftComposer = ({
  workspaceId,
  artifactId,
  title,
  fromAddress,
  onClose
}: {
  workspaceId: string;
  /** Null while no email is open. */
  artifactId: string | null;
  title: string;
  /** The page's Send from, which the composer shows as the email's From. */
  fromAddress: string;
  onClose: () => void;
}) => (
  <Slideout size="4xl" showing={!!artifactId} title={title} onClose={onClose}>
    {artifactId && (
      <WorkspaceEmail
        workspaceId={workspaceId}
        artifactId={artifactId}
        fromAddress={fromAddress}
        onClose={onClose}
      />
    )}
  </Slideout>
);

const WorkspaceEmail = (props: {
  workspaceId: string;
  artifactId: string;
  fromAddress: string;
  onClose: () => void;
}) => {
  const { data, loading, refetch } = useGetCaraWorkspaceQuery({
    variables: { id: props.workspaceId }
  });
  const workspace = data?.caraWorkspace;
  if (workspace) {
    return <LiveEmail {...props} loadedWorkspace={workspace} />;
  }
  if (loading) {
    return <LoadingEmail />;
  }
  return (
    <InlineNotice
      tone="error"
      title="We couldn't load this email."
      description="Check your connection and try again."
      actions={
        <Button color="white" onClick={() => void refetch()}>
          Try again
        </Button>
      }
    />
  );
};

const LiveEmail = ({
  loadedWorkspace,
  artifactId,
  fromAddress,
  onClose
}: {
  loadedWorkspace: CaraWorkspaceFieldsFragment;
  artifactId: string;
  fromAddress: string;
  onClose: () => void;
}) => {
  const { workspace } = useCaraWorkspaceSubscription(loadedWorkspace);
  const focus = useArtifactFocus(workspace);
  const isComposerEnabled = useMailboxComposerEnabled();
  const { openArtifactFocusDialogUnchecked, showArtifactFocusDialog } = focus;
  const newestVersion = newestArtifactVersion(workspace.artifacts, artifactId);
  // Opened on the version the workspace holds then; the composer follows it from there.
  React.useEffect(() => {
    if (!showArtifactFocusDialog && newestVersion && isComposerEnabled) {
      openArtifactFocusDialogUnchecked(newestVersion);
    }
  }, [showArtifactFocusDialog, newestVersion, isComposerEnabled, openArtifactFocusDialogUnchecked]);

  // The composer needs a mailbox the agent can send from, as on the workspace page.
  if (!isComposerEnabled) {
    return (
      <InlineNotice
        tone="warning"
        title="Connect a mailbox before you edit these emails."
        description="Bulk emails go out from your own mailbox, so replies come back to you."
        actions={
          <Button color="white" href={MAILBOX_SETTINGS_PATH}>
            Go to Settings &gt; Mailboxes
          </Button>
        }
      />
    );
  }
  if (!newestVersion) {
    return (
      <InlineNotice
        tone="error"
        title="We couldn't load this email."
        description="It's no longer in its workspace. Open the workspace to see where it went."
      />
    );
  }
  if (!showArtifactFocusDialog || !focus.currentArtifact) {
    return <LoadingEmail />;
  }
  return (
    <div className="h-full">
      <ArtifactFocusDialog
        canUpdate
        workspace={workspace}
        artifact={focus.currentArtifact}
        htmlDocumentTemplates={[]}
        agencyProducerInformations={[]}
        userProducerInformations={[]}
        onClose={onClose}
        onVersionChange={focus.setCurrentArtifact}
        onVersionCreated={focus.showArtifactVersion}
        emailBodyHydrating={focus.emailBodyHydrating}
        bulkSendFromAddress={fromAddress}
      />
    </div>
  );
};
